import React, { useMemo } from 'react';
import { Search } from 'lucide-react';
import type { FaturaPainel, FiltrosPainel, PeriodoFiltro, StatusBoleto } from '../types';
import { useIndiceUsinas } from '../../../../src/hooks/useIndiceUsinas';
import { digitosUc } from '../../../../src/lib/dashboard/painelCeo';

interface Props {
  filtros: FiltrosPainel;
  onChange: (filtros: FiltrosPainel) => void;
  dados: FaturaPainel[]; // usado só para montar as opções (UGs, autocomplete de cliente)
}

const OPCOES_PERIODO: { value: PeriodoFiltro; label: string }[] = [
  { value: 'ultimos12', label: 'Últimos 12 meses' },
  { value: 'ultimos6', label: 'Últimos 6 meses' },
  { value: 'ultimos3', label: 'Últimos 3 meses' },
  { value: 'mesAtual', label: 'Mês atual' },
  { value: 'mesAnterior', label: 'Mês anterior' },
  { value: 'trimestreAtual', label: 'Trimestre atual' },
  { value: 'trimestreAnterior', label: 'Trimestre anterior' },
  { value: 'anoAtual', label: 'Ano atual' },
  { value: 'anoAnterior', label: 'Ano anterior' },
  { value: 'historico', label: 'Todo o histórico' },
];

/** Rótulo legível do período, para os blocos que precisam dizer sobre que
 *  janela o número foi calculado (ex.: o card "Vencido"). */
export function rotuloPeriodo(periodo: PeriodoFiltro): string {
  return OPCOES_PERIODO.find((o) => o.value === periodo)?.label ?? 'Período atual';
}

const OPCOES_STATUS: { value: FiltrosPainel['status']; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'Pago', label: 'Pago' },
  { value: 'Em aberto', label: 'Em aberto' },
  { value: 'Vencido', label: 'Vencido' },
  { value: 'Parcelado', label: 'Parcelado' },
  { value: 'Cancelado', label: 'Cancelado' },
  { value: 'Quitado', label: 'Quitado' },
  { value: 'Sem cobrança', label: 'Sem cobrança' },
  { value: 'Sem injeção', label: 'Sem injeção' },
  { value: 'Cobrança agrupada', label: 'Cobrança agrupada' },
];

const selectCls =
  'px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40';

const FiltrosGlobais: React.FC<Props> = ({ filtros, onChange, dados }) => {
  // A UG vem crua da conta ("000406019101257"); o nome da usina é o que se lê (#6402).
  const indiceUsinas = useIndiceUsinas();
  const nomeDaUg = (ug: string) => indiceUsinas.porUc.get(digitosUc(ug))?.nome ?? ug;
  const topUgs = useMemo(() => {
    const contagem = new Map<string, number>();
    dados.forEach((f) => {
      if (f.unidade_geradora_1) contagem.set(f.unidade_geradora_1, (contagem.get(f.unidade_geradora_1) || 0) + 1);
    });
    return Array.from(contagem.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([nome]) => nome);
  }, [dados]);

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
      <select
        className={selectCls}
        value={filtros.periodo}
        onChange={(e) => onChange({ ...filtros, periodo: e.target.value as PeriodoFiltro })}
      >
        {OPCOES_PERIODO.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <select
        className={selectCls}
        value={filtros.unidadeGeradora}
        onChange={(e) => onChange({ ...filtros, unidadeGeradora: e.target.value })}
      >
        <option value="todas">Todas as UGs</option>
        <option value="outras">Outras / sem UG</option>
        {topUgs.map((ug) => (
          <option key={ug} value={ug}>{nomeDaUg(ug)}</option>
        ))}
      </select>

      <select
        className={selectCls}
        value={filtros.status}
        onChange={(e) => onChange({ ...filtros, status: e.target.value as StatusBoleto | 'todos' })}
      >
        {OPCOES_STATUS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          list="painel-executivo-clientes"
          placeholder="Buscar cliente..."
          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
          value={filtros.clienteBusca}
          onChange={(e) => onChange({ ...filtros, clienteBusca: e.target.value })}
        />
        <datalist id="painel-executivo-clientes">
          {Array.from(new Set(dados.map((f) => f.cliente_nome).filter(Boolean))).slice(0, 200).map((nome) => (
            <option key={nome} value={nome!} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

export default FiltrosGlobais;
